import React from 'react';

import { get } from '@lumapps/constants';
import { isFeatureEnabled } from '@lumapps/features';
import { Action } from '@lumapps/lumx-front-office/components/ContextualActions';
import { mdiTranslate } from '@lumapps/lumx/icons';
import { useSelector } from '@lumapps/redux/react';
import { useLocation } from '@lumapps/router';
import { getKeyForLanguage, useTranslate } from '@lumapps/translations';
import { currentLanguageSelector } from '@lumapps/translations/ducks/selectors';
import { getConnectedUserLanguage, isConnected } from '@lumapps/user/ducks/selectors';
import { useWidgetsAutoTranslate } from '@lumapps/widget-base/hooks/useWidgetsAutoTranslate';

import { AUTO_TRANSLATION } from '../keys';

const constants = get();

/**
 * Hook that return a translation action props to be used on the ContextualActions component
 *
 * @param onTranslation - callback to be called when clicking on the translation action
 * @param actionProps - additional props to be passed on the action (like the data attributes)
 */
export const useTranslationContextualAction = (
    parameters?: Parameters<typeof useWidgetsAutoTranslate>[0],
    onAutoTranslateOverride?: (isTranslationActive: boolean) => void,
): { translationAction?: Action; isTranslationActive: boolean; onResetTranslation: () => void } | undefined => {
    const { translateKey, translateAndReplace } = useTranslate();

    const userLanguage = useSelector(getConnectedUserLanguage);
    const currentLanguage = useSelector(currentLanguageSelector);
    const isUserConnected = useSelector(isConnected);
    const isEndUserTranslationEnabled = useSelector(isFeatureEnabled('translation-end-user'));
    const { pathname } = useLocation();

    const [isTranslationActive, setIsTranslationActive] = React.useState(false);

    const { onAutoTranslate, onReset, onHardReset } = useWidgetsAutoTranslate(parameters);

    /**
     * Auto translation should only be available for authenticated users and when the correct FF is enabled
     * In legacy app, the translation feature visibility is set by angular.
     */
    const isTranslateActionVisible = isEndUserTranslationEnabled && isUserConnected;
    const translateLanguage = userLanguage || currentLanguage || constants.defaultLanguage;

    const translateLanguageLabel = React.useMemo(
        () => translateKey(getKeyForLanguage(translateLanguage)),
        [translateLanguage, translateKey],
    );
    const translationLabel = React.useMemo(
        () =>
            isTranslationActive
                ? translateAndReplace(AUTO_TRANSLATION.CONTEXTUAL_ACTIONS_TRANSLATION_ON_ACTION, {
                      LANGUAGE_LABEL: translateLanguageLabel,
                  })
                : translateAndReplace(AUTO_TRANSLATION.CONTEXTUAL_ACTIONS_TRANSLATION_OFF_ACTION, {
                      LANGUAGE_LABEL: translateLanguageLabel,
                  }),
        [translateLanguageLabel, isTranslationActive, translateAndReplace],
    );

    // Ensure the translations are reset on route change
    React.useEffect(() => {
        onHardReset();
        setIsTranslationActive(false);
    }, [onHardReset, pathname]);

    if (!isTranslateActionVisible) {
        return undefined;
    }

    const resetTranslation = () => {
        onReset();
        setIsTranslationActive(false);
    };

    const handleOnTranslation = async () => {
        // Should use the old translation system on legacy content
        if (onAutoTranslateOverride) {
            await onAutoTranslateOverride(!isTranslationActive);
            setIsTranslationActive(!isTranslationActive);
            return;
        }
        if (isTranslationActive) {
            resetTranslation();
        } else {
            await onAutoTranslate();
            setIsTranslationActive(true);
        }
    };

    return {
        translationAction: {
            isSelected: isTranslationActive,
            iconSvgPath: mdiTranslate,
            labelKey: translationLabel,
            action: handleOnTranslation,
        },
        isTranslationActive,
        onResetTranslation: resetTranslation,
    };
};
